/**
 * datevExportAccess — geteilte Zugriffslogik für den DATEV-Export (M7).
 *
 * Single source of truth dafür, WER den DATEV-Export sehen und benutzen darf.
 * Wird von `App` (Route-Registrierung), `MainLayout` (Nav-Eintrag, Desktop +
 * Mobile) und `DatevExportPage` (Inhalts-Gate) gemeinsam genutzt — kein
 * Copy-Paste der Flag-/Allowlist-Bedingung.
 *
 * Wie der Belegscanner (`receiptScanAccess.ts`) ist der DATEV-Export ab GA ein
 * **Ultra**-Differenzierer — ABER ausschließlich im **Owner-Kontext**
 * (architecture-pricing-repackaging.md §1/§3.2/§3.2b-FIX). Bei allgemeiner
 * Freigabe gilt im Owner-Kontext `FLAG ∧ tier==='ultra'` — NICHT mehr „für ALLE
 * eingeloggten Nutzer". Im **Advisor-Kontext** (Steuerberater im Mandanten-Tenant)
 * bleibt das Gate rein Loop-B-/Kanzlei-gegatet OHNE Owner-Tier-Term: ein StB darf
 * NIE durch den fehlenden Ultra-Status seines Mandanten aus dem DATEV-Export
 * ausgesperrt werden (§3.2b). Deshalb der explizite `isAdvisorContext`-Parameter
 * — 3-Param-Parität mit `canUseDatevBelege`/`canUseInboundDatev`, damit die
 * gesamte DATEV-Kette (inkl. Seiten-Gate + Nav-Eintrag) den Advisor konsistent
 * durchlässt (T4b, §3.2b-FIX: sonst sperrt das Seiten-umgebende Export-Gate den
 * StB aus, BEVOR der Belege-Bypass je erreichbar ist).
 *
 * Zwei Freischalt-Pfade (fail-closed — Default ist KEIN Zugriff):
 *   1. Beta-Allowlist: explizit gelistete Test-Account-UIDs sehen den Export
 *      LIVE, unabhängig vom Env-Flag und Tier (kontrollierte Erprobung).
 *   2. Allgemeine Freigabe: `REACT_APP_DATEV_EXPORT_ENABLED='true'` UND Ultra-Tier.
 *
 * Inert, solange das Flag live `false`/unset ist ⇒ nur die Beta-Allowlist greift,
 * byte-identisch zum IST.
 *
 * @module datevExportAccess
 */

import type { Tier } from './entitlements';

/**
 * Beta-Erprobung DATEV-Export — entfernen/erweitern, wenn allgemein freigegeben.
 *
 * Nur diese UIDs erhalten Zugriff unabhängig vom Env-Flag. HARTKODIERT für
 * die kontrollierte Live-Erprobung; vor allgemeiner Freigabe (Flag = 'true'
 * für alle Nutzer) wieder leeren.
 */
export const DATEV_EXPORT_BETA_UIDS: readonly string[] = ['b3DRR1Cj63cRjdJg5VCEUpBL7hk1'];

/**
 * Allgemeines Feature-Flag. Fail-closed: nur bei explizitem `'true'` aktiv.
 * Modul-Konstante, einmalig beim Bundle-Load ausgewertet.
 */
export const DATEV_EXPORT_FLAG: boolean = process.env.REACT_APP_DATEV_EXPORT_ENABLED === 'true';

/**
 * Entscheidet fail-closed, ob der DATEV-Export für einen Scope sehen/benutzen darf.
 *
 * Reihenfolge (identisch zu `canUseDatevBelege`/`canUseInboundDatev`, §3.2b):
 *   1. Beta-Allowlist-Treffer ⇒ `true` (flag- und tier-unabhängig).
 *   2. Advisor-Kontext ⇒ reines `DATEV_EXPORT_FLAG` (KEIN Owner-Tier-Term — ein
 *      StB wird nie durch den Tier seines Mandanten gesperrt, §3.2b).
 *   3. Owner-Kontext ⇒ `DATEV_EXPORT_FLAG ∧ tier==='ultra'` (Ultra-Differenzierer).
 *
 * @param scopeUid - `effectiveScopeUid` aus `useAuth()` (Tenant-Scope; oder
 *              null/undefined, solange noch nicht aufgelöst). Für den Owner
 *              identisch zu `currentUser.uid`.
 * @param tier - effektiver Owner-Tier (`resolveTier(sellerData)` aus
 *               `entitlements.ts`, §3.2). Nur im Owner-Kontext ausgewertet.
 * @param isAdvisorContext - `true`, wenn der aktuelle Nutzer ein Steuerberater im
 *               Mandanten-Kontext ist (`!isOwnScope` / readOnly, §3.2b-FIX) — NICHT
 *               `useIsAdvisorContext()` (eigener Kanzlei-Scope = Owner-Pfad). Dann
 *               KEIN Owner-Tier-Term, reines Loop-B-/Flag-Gate.
 * @returns `true`, wenn `scopeUid` auf der Beta-Allowlist steht ODER (im
 *          Advisor-Kontext) das Flag aktiv ist ODER (im Owner-Kontext) das Flag
 *          aktiv ist UND der Owner den Ultra-Tier hat. Sonst `false`.
 */
export function canUseDatevExport(
  scopeUid: string | null | undefined,
  tier: Tier,
  isAdvisorContext: boolean,
): boolean {
  if (!!scopeUid && DATEV_EXPORT_BETA_UIDS.includes(scopeUid)) {
    return true;
  }
  // §3.2b Advisor-Kontext: reines Loop-B-/Kanzlei-Gate, KEIN Owner-Tier-Term.
  if (isAdvisorContext) {
    return DATEV_EXPORT_FLAG;
  }
  // Owner-Kontext: allgemeine Freigabe erst ab Ultra.
  return DATEV_EXPORT_FLAG && tier === 'ultra';
}
